import { createSwrHook } from '@spotify-internal/creator-data-loading';
import { webgateFetch, WEBGATE_DOMAIN } from '@mrkt/features/webgate-fetch';
import { logRosterPrimerFailure } from '../logging/sloLogger';

export type RosterSearchParams = {
  // Not used currently as everything is hardcoded
  // sortColumn?: 'artist' | 'releaseDate';
  // sortDirection?: 'asc' | 'desc';
  limit: 20;
  offset: number;
};

export type ActionTypes =
  | 'UNKNOWN'
  | 'VIEW_FULL_PITCH'
  | 'VIEW_LIMITED_PITCH'
  | 'CREATE_PITCH'
  | 'NO_PERMISSION'
  | 'NO_ELIGIBLE_MUSIC';

export type RosterAction = {
  type: 'NMS' | 'UNKNOWN_TYPE';
  action: ActionTypes;
  url?: string;
};

export type RosterSearchArtist = {
  id: string;
  name: string;
  // the BE will send an empty string if there is no avatar image for this artist
  image_url?: string;
  actions?: RosterAction[];
  display_date?: string;
};

export type RosterSearchPayload = {
  artists: RosterSearchArtist[];
  total: string;
};

type RosterSearchSuccessResponse = {
  status: 'success';
  requestTime: number;
  payload: RosterSearchPayload;
};

type RosterSearchForbiddenResponse = {
  status: 'forbidden';
};

export type RosterSearchResponse =
  | RosterSearchForbiddenResponse
  | RosterSearchSuccessResponse;

const ROSTER_URL = `${WEBGATE_DOMAIN}/roster-view-service/v1/artists`;

// The ordering of these params matters!
// To cache hit when preloading, ensure that useRosterSearchParams
// and this object have defined their keys in the same order top to bottom.
// Oh! the Perils of JSON.stringify() as a cache key...
const initialParams: RosterSearchParams = {
  limit: 20,
  offset: 0,
};

const [useGetRosterData, preloadRosterData] = createSwrHook<
  RosterSearchResponse,
  RosterSearchParams
>(
  async (params: RosterSearchParams) => {
    const { offset } = params;

    const response = await webgateFetch(`${ROSTER_URL}?offset=${offset}`);

    if (response.status === 200) {
      return {
        status: 'success',
        requestTime: Date.now(),
        payload: (await response.json()) as RosterSearchPayload,
      };
    }

    if (response.status === 403) {
      return {
        status: 'forbidden',
      };
    }

    throw new Error('Failed to roster search');
  },
  {
    shouldRetryOnError: false,
  },
);

export function useRosterSearch(params: RosterSearchParams = initialParams) {
  return useGetRosterData(params);
}

// ensure that this only gets called once ever.
let hasBeenInvoked = false;
export async function primeInitialPage() {
  // prime the network cache before the user gets to the page
  // to make the first render seem instantaneous. If there is
  // an error, log it to sentry and send an SLO error event.
  if (hasBeenInvoked) return;
  hasBeenInvoked = true;

  try {
    await preloadRosterData(initialParams);
  } catch (error) {
    logRosterPrimerFailure(error);
  }
}
